import rawConfig from "../data/gamification.json";

export const GAMIFICATION_STORAGE_KEY =
  "drugSyncGamification_v1";

const QUEUE_KEY = "gamificationQueuedActions";

/** Set on register page when user uses "Continue with Google"; consumed when registration API succeeds. */
const SESSION_GOOGLE_ASSIST_REGISTER =
  "drugSyncGoogleAssistRegisterPending";

export function markGoogleAssistRegisterPending() {
  try {
    sessionStorage.setItem(
      SESSION_GOOGLE_ASSIST_REGISTER,
      "1"
    );
  } catch {
    /* ignore */
  }
}

/** @returns {boolean} whether Google assist was pending (and clears the flag) */
export function takeGoogleAssistRegisterPending() {
  try {
    const v = sessionStorage.getItem(
      SESSION_GOOGLE_ASSIST_REGISTER
    );

    sessionStorage.removeItem(
      SESSION_GOOGLE_ASSIST_REGISTER
    );

    return v === "1";
  } catch {
    return false;
  }
}

/** @returns {typeof rawConfig} */
export function getGamificationConfig() {
  return rawConfig;
}

function todayLocal() {
  const d = new Date();

  return `${d.getFullYear()}-${String(
    d.getMonth() + 1
  ).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;
}

function loadRoot() {
  try {
    const raw = localStorage.getItem(
      GAMIFICATION_STORAGE_KEY
    );

    if (!raw) {
      return { wallets: {} };
    }

    const parsed = JSON.parse(raw);

    if (!parsed || typeof parsed !== "object") {
      return { wallets: {} };
    }

    if (!parsed.wallets) {
      return { wallets: {} };
    }

    return parsed;
  } catch {
    return { wallets: {} };
  }
}

function saveRoot(root) {
  localStorage.setItem(
    GAMIFICATION_STORAGE_KEY,
    JSON.stringify(root)
  );
}

function emptyWallet() {
  return {
    balance: 0,
    ledger: [],
    awardedOnce: [],
    lastDaily: {},
    repeatCounts: {},
    redemptions: [],
  };
}

function normalizeWallet(w) {
  const base = emptyWallet();

  if (!w || typeof w !== "object") {
    return base;
  }

  return {
    balance:
      typeof w.balance === "number"
        ? w.balance
        : 0, // NOSONAR

    ledger: Array.isArray(w.ledger)
      ? w.ledger
      : [],

    awardedOnce: Array.isArray(w.awardedOnce)
      ? w.awardedOnce
      : [],

    lastDaily:
      w.lastDaily &&
      typeof w.lastDaily === "object"
        ? w.lastDaily
        : {},

    repeatCounts:
      w.repeatCounts &&
      typeof w.repeatCounts === "object"
        ? w.repeatCounts
        : {},

    redemptions: Array.isArray(w.redemptions)
      ? w.redemptions
      : [],
  };
}

export function getWallet(userId) {
  if (!userId) {
    return emptyWallet();
  }

  const root = loadRoot();

  return normalizeWallet(
    root.wallets[userId]
  );
}

function persistWallet(userId, wallet) {
  const root = loadRoot();

  root.wallets[userId] = wallet;

  saveRoot(root);
}

function makeLedgerId() {
  return `${Date.now()}-${Math.random() // NOSONAR - client-side localStorage dedup key only, no cryptographic use
    .toString(36)
    .slice(2, 10)}`;
}

function countEarnsToday(wallet, actionId) {
  const day = todayLocal();

  return wallet.ledger.filter(
    (e) =>
      e.kind === "earn" &&
      e.ref === actionId &&
      typeof e.at === "string" &&
      e.at.slice(0, 10) === day
  ).length;
}

function validateEarnAction(
  userId,
  actionId,
  rule
) {
  if (!userId || !actionId) {
    return {
      ok: false,
      reason: "missing_user_or_action",
    };
  }

  if (!rule) {
    return {
      ok: false,
      reason: "unknown_action",
    };
  }

  const coins = rule.coins ?? 0;

  if (coins <= 0) {
    return {
      ok: false,
      reason: "zero_coins",
    };
  }

  return null;
}

function validateOnceRule(
  wallet,
  actionId
) {
  if (
    wallet.awardedOnce.includes(actionId)
  ) {
    return {
      ok: false,
      reason: "already_awarded",
    };
  }

  return null;
}

function validateDailyRule(
  wallet,
  actionId,
  rule
) {
  const maxPerDay =
    typeof rule.maxPerDay === "number"
      ? rule.maxPerDay
      : 0;

  if (maxPerDay > 0) {
    const todayCount = countEarnsToday(
      wallet,
      actionId
    );

    if (todayCount >= maxPerDay) {
      return {
        ok: false,
        reason: "daily_cap",
      };
    }

    return null;
  }

  if (
    wallet.lastDaily[actionId] ===
    todayLocal()
  ) {
    return {
      ok: false,
      reason: "already_today",
    };
  }

  return null;
}

function validateRepeatableRule(
  wallet,
  actionId,
  rule
) {
  const count =
    wallet.repeatCounts[actionId] || 0;

  if (
    typeof rule.maxTimes === "number" &&
    count >= rule.maxTimes
  ) {
    return {
      ok: false,
      reason: "repeat_cap",
    };
  }

  return null;
}

function validateFrequencyRule(
  wallet,
  actionId,
  rule
) {
  if (rule.frequency === "once") {
    return validateOnceRule(
      wallet,
      actionId
    );
  }

  if (rule.frequency === "daily") {
    return validateDailyRule(
      wallet,
      actionId,
      rule
    );
  }

  if (rule.frequency === "repeatable") {
    return validateRepeatableRule(
      wallet,
      actionId,
      rule
    );
  }

  return null;
}

function createLedgerEntry(
  actionId,
  rule,
  coins
) {
  return {
    id: makeLedgerId(),
    at: new Date().toISOString(),
    delta: coins,
    kind: "earn",
    ref: actionId,
    label: rule.label || actionId,
  };
}

function updateWalletForFrequency(
  wallet,
  actionId,
  rule
) {
  if (rule.frequency === "once") {
    wallet.awardedOnce = [
      ...new Set([
        ...wallet.awardedOnce,
        actionId,
      ]),
    ];

    return;
  }

  if (rule.frequency === "daily") {
    wallet.lastDaily = {
      ...wallet.lastDaily,
      [actionId]: todayLocal(),
    };

    return;
  }

  if (rule.frequency === "repeatable") {
    const prev =
      wallet.repeatCounts[actionId] || 0;

    wallet.repeatCounts = {
      ...wallet.repeatCounts,
      [actionId]: prev + 1,
    };
  }
}

/**
 * @param {string} userId
 * @param {string} actionId
 * @returns {{ ok: boolean, reason?: string, amount?: number, wallet?: any }}
 */
export function tryEarnUserAction(
  userId,
  actionId
) {
  // NOSONAR - Legacy complexity/nesting/assignment accepted

  const cfg = getGamificationConfig();

  const rule = cfg.earnRules?.find(
    (r) => r.id === actionId
  );

  const validationError =
    validateEarnAction(
      userId,
      actionId,
      rule
    );

  if (validationError) {
    return validationError;
  }

  let wallet = getWallet(userId);

  const frequencyError =
    validateFrequencyRule(
      wallet,
      actionId,
      rule
    );

  if (frequencyError) {
    return frequencyError;
  }

  const coins = rule.coins ?? 0;

  const entry = createLedgerEntry(
    actionId,
    rule,
    coins
  );

  wallet = {
    ...wallet,
    balance: wallet.balance + coins,
    ledger: [entry, ...wallet.ledger].slice(
      0,
      200
    ),
  };

  updateWalletForFrequency(
    wallet,
    actionId,
    rule
  );

  persistWallet(userId, wallet);

  return {
    ok: true,
    amount: coins,
    wallet,
  };
}

export function queueGamificationActions(
  actionIds
) {
  if (
    !Array.isArray(actionIds) ||
    !actionIds.length
  ) {
    return;
  }

  try {
    const prev = JSON.parse(
      sessionStorage.getItem(QUEUE_KEY) ||
        "[]"
    );

    const arr = Array.isArray(prev)
      ? prev
      : [];

    const next = [
      ...new Set([
        ...arr,
        ...actionIds,
      ]),
    ];

    sessionStorage.setItem(
      QUEUE_KEY,
      JSON.stringify(next)
    );
  } catch {
    sessionStorage.setItem(
      QUEUE_KEY,
      JSON.stringify([
        ...new Set(actionIds),
      ])
    );
  }
}

function readQueue() {
  try {
    const raw =
      sessionStorage.getItem(QUEUE_KEY);

    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw);

    return Array.isArray(parsed)
      ? parsed
      : [];
  } catch {
    return [];
  }
}

function clearQueue() {
  sessionStorage.removeItem(QUEUE_KEY);
}

/**
 * Apply queued actions (e.g. after registration before userId was known).
 */
export function drainGamificationQueue(
  userId
) {
  const ids = readQueue();

  clearQueue();

  const results = [];

  for (const id of ids) {
    const r = tryEarnUserAction(
      userId,
      id
    );

    if (r.ok) {
      results.push({
        actionId: id,
        amount: r.amount,
      });
    }
  }

  return results;
}

export function tryRedeemReward(
  userId,
  rewardId
) {
  if (!userId || !rewardId) {
    return {
      ok: false,
      reason: "missing",
    };
  }

  const cfg = getGamificationConfig();

  const reward = cfg.rewards?.find(
    (x) => x.id === rewardId
  );

  if (!reward) {
    return {
      ok: false,
      reason: "unknown_reward",
    };
  }

  const cost = reward.cost ?? 0;

  if (cost <= 0) {
    return {
      ok: false,
      reason: "invalid_cost",
    };
  }

  let wallet = getWallet(userId);

  if (wallet.balance < cost) {
    return {
      ok: false,
      reason:
        "insufficient_balance",
    };
  }

  const already =
    wallet.redemptions.some(
      (r) => r.rewardId === rewardId
    );

  if (already) {
    return {
      ok: false,
      reason: "already_redeemed",
    };
  }

  const entry = {
    id: makeLedgerId(),
    at: new Date().toISOString(),
    delta: -cost,
    kind: "redeem",
    ref: rewardId,
    label: reward.title,
  };

  wallet = {
    ...wallet,
    balance: wallet.balance - cost,
    ledger: [entry, ...wallet.ledger].slice(
      0,
      200
    ),
    redemptions: [
      ...wallet.redemptions,
      {
        rewardId,
        at: entry.at,
        title: reward.title,
      },
    ],
  };

  persistWallet(userId, wallet);

  return {
    ok: true,
    wallet,
    reward,
  };
}

/**
 * Run login-time awards: queue from signup, first login, daily login.
 */
export function runLoginGamification(
  userId
) {
  const queueGranted =
    drainGamificationQueue(userId);

  const firstLogin =
    tryEarnUserAction(
      userId,
      "first_login"
    );

  const dailyLogin =
    tryEarnUserAction(
      userId,
      "daily_login"
    );

  const loginPlusOne =
    tryEarnUserAction(
      userId,
      "login_plus_one"
    );

  return {
    queueGranted,
    firstLogin,
    dailyLogin,
    loginPlusOne,
  };
}

export function snapshotForUser(
  userId
) {
  const w = getWallet(userId);

  return {
    balance: w.balance,
    ledger: w.ledger.slice(0, 50),
    redemptions: w.redemptions,
  };
}

/** Preserve gamification blob when clearing storage (logout). */
export function readGamificationBlob() {
  try {
    return localStorage.getItem(
      GAMIFICATION_STORAGE_KEY
    );
  } catch {
    return null;
  }
}

export function restoreGamificationBlob(
  blob
) {
  if (blob === null) {
    return;
  }

  try {
    localStorage.setItem(
      GAMIFICATION_STORAGE_KEY,
      blob
    );
  } catch {
    /* ignore */
  }
}